import React, { ReactNode, Ref, useCallback, useContext, useEffect, useRef, useState } from 'react';
import GIcon from '../GIcon';
import { Dialog, Fade, Tooltip } from '@mui/material';
import IconButton from '../../icons/IconButton';
import { Context } from '../ChatPanel/ChatContext';
import { AudioVisualizerProps, IContext, IMessage, IMessagePost } from '../../types';
import { useAtomInstance, useAtomValue } from '@zedux/react';
import { activeTopicAtom } from '../../atoms/atoms';
import { messageReceiverAtom } from '../../atoms/messageReceiverAtom';
import cx from 'classix';





function useRecorder() {
    const chunksRef = useRef<Blob[]>([]);
    const audioContextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const recorderRef = useRef<MediaRecorder | null>(null);
    const dataArrayRef = useRef<Float32Array | null>(null);
    const sendFileRef = useRef<boolean>(false);

    const start = useCallback(async (sendMessage:
        (a: IMessagePost) => void,
        activeTopicID: number,
        replyMode: IMessage | null,
        setReplyMode: (message: IMessage | null) => void,
        context: IContext) => {
        try {
            if (!audioContextRef.current || audioContextRef.current.state === "closed") {
                audioContextRef.current = new AudioContext();
                analyserRef.current = audioContextRef.current.createAnalyser();
            }
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

            let input = audioContextRef.current.createMediaStreamSource(stream);
            let filter = audioContextRef.current.createBiquadFilter();

            analyserRef.current!.fftSize = 32;
            filter.Q.value = 10.0;
            filter.frequency.value = 60.0;
            filter.type = "notch";


            input.connect(filter);
            filter.connect(analyserRef.current!);
            recorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
            recorderRef.current.start(500);
            recorderRef.current.ondataavailable = (currentChunk) => {
                if (currentChunk.data.size > 0) {
                    chunksRef.current.push(currentChunk.data);
                }
            };

            recorderRef.current.onstop = async (e) => {
                if (chunksRef.current.length !== 0 && sendFileRef.current) {
                    const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
                    sendMessage({
                        topic_id: activeTopicID,
                        context: context,
                        text: '',
                        file_content: blob,
                        filename: `audio_${Date.now()}.webm`,
                        reply_id: replyMode?.message_id ?? null,
                        mime_type: blob.type,
                        created_at: Date.now(),
                        forward_id: null,
                    } as IMessagePost);
                    if (setReplyMode) {
                        setReplyMode(null)
                    }
                }
                chunksRef.current = [];
                audioContextRef.current?.close();
                sendFileRef.current = false;
            };
            dataArrayRef.current = new Float32Array(analyserRef.current!.frequencyBinCount);
            return true;
        } catch (error: any) {
            console.error("Error accessing microphone:", error);
            if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
                alert("میکروفون یافت نشد. لطفا از اتصال میکروفون خود مطمئن شوید.");
            } else if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                alert("دسترسی به میکروفون مسدود شده است. لطفا در تنظیمات مرورگر اجازه دسترسی بدهید.");
            } else {
                alert("خطا در دسترسی به میکروفون: " + error.message);
            }
            return false;
        }
    }, []);

    const stop = useCallback((send: boolean) => {
        sendFileRef.current = send;
        if (recorderRef.current && recorderRef.current.state !== "inactive") {
            recorderRef.current.stop();
        }
    }, []);

    const clearChunks = useCallback(() => {
        chunksRef.current = [];
    }, []);

    return { start, stop, clearChunks, analyserRef, dataArrayRef };
}


const WIDTH = 300;
const HEIGHT = 300;

const AudioVisualizer = React.forwardRef<HTMLDivElement, AudioVisualizerProps>(({ onClick, recording, replyMode, setReplyMode, ...rest }, ref) => {

    // const { replyMessage: replyId } = useContext(Context);
    const [counter, setCounter] = useState(0);
    const animationRef = useRef<number>(null);
    // const canvasRef = useRef<HTMLCanvasElement>(null);
    const interValRef = useRef<number>(0);

    const { context } = useContext(Context)
    const activeTopicId = useAtomValue(activeTopicAtom, [context])
    const { sendMessage } = useAtomInstance(messageReceiverAtom, [context]).exports

    const { start, stop: stopRecorder, clearChunks, analyserRef, dataArrayRef } = useRecorder();

    useEffect(() => {
        return () => {
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
            if (interValRef.current) clearInterval(interValRef.current);
        };
    }, []);

    // const draw = () => {
    //     const canvas = canvasRef.current;
    //     if (!canvas) return;
    //     const ctx = canvas.getContext("2d")
    //     if (!ctx) return;
    //     ctx.globalAlpha = 0.5;
    //     ctx.fillStyle = "#ff0055"
    //     const x = WIDTH / 2;
    //     const y = HEIGHT / 2;
    //     const pi2 = Math.PI * 2;
    //     const renderFrame = () => {
    //         animationRef.current = requestAnimationFrame(renderFrame)

    //         if (analyserRef.current && dataArrayRef.current) {
    //             analyserRef.current.getFloatFrequencyData(dataArrayRef.current as any);
    //             ctx.clearRect(0, 0, WIDTH, HEIGHT);
    //             ctx.beginPath()
    //             // Ensure radius is positive. (decibels usually range from -100 to 0)
    //             const volume = Math.max(...dataArrayRef.current);
    //             const radius = Math.max(0, (volume + 100) * 1.5);
    //             ctx.arc(x, y, radius, 0, pi2)
    //             ctx.fill()
    //             ctx.closePath()
    //         }
    //     }
    //     renderFrame()
    // };


    const stop = (send: boolean) => {
        cancelAnimationFrame(animationRef.current!)
        clearInterval(interValRef.current)
        setCounter(0)
        // const canvas = canvasRef.current;
        // setCounter(0)
        // if (canvas) {
        //     const ctx = canvas!.getContext("2d")
        //     ctx?.clearRect(0, 0, WIDTH, HEIGHT);
        // }
        stopRecorder(send)
        onClick(false)
    };

    return (
        <div ref={ref}
            {...rest}
             className={cx(
            "flex flex-row justify-center items-center content-center self-end",
            recording ? 'grow justify-between' : ''
        )}

        >
            {recording ?
                <>
                    <span>{Math.floor(counter / 60).toString().padStart(2, "0")}:{(counter % 60).toString().padStart(2, "0")}</span>
                    <div className='relative mx-2 w-4 h-4'>
                        <div className='absolute rounded-full w-4 h-4 record_with_ripple' />
                        <span className='absolute bg-red-500 rounded-full size-4' />
                    </div>

                    <Tooltip
                        className='mx-5'
                        slots={{
                            transition: Fade,
                        }}
                        slotProps={{
                            transition: { timeout: 600 },
                        }} placement='top' arrow title="حذف صوتی">
                        <GIcon style={{
                            position: 'relative', // so it stays in layout
                            zIndex: 20,
                        }} className='hover:text-red-600 touch-manipulation cursor-pointer select-none' onClick={(e) => { e.preventDefault(); onClick(false); clearChunks(); stop(false) }} name="delete" />
                    </Tooltip>
                </>

                :
                <Tooltip
                    slots={{
                        transition: Fade,
                    }}
                    slotProps={{
                        transition: { timeout: 600 },
                    }} placement='top' arrow title="پیام صوتی">
                    <GIcon style={{
                        position: 'relative', // so it stays in layout
                        zIndex: 20,
                    }} className='self-end hover:text-sky-500 touch-manipulation cursor-pointer select-none' onClick={async (e) => {
                        e.preventDefault()
                        const success = await start(sendMessage, activeTopicId!, replyMode, setReplyMode, context)
                        if (success) {
                            interValRef.current = setInterval(() => setCounter((counter) => counter + 1), 1000) as any;
                            onClick(true);
                            // draw()
                        }
                    }} name="mic" />

                </Tooltip>
            }
            <div
                className={cx(
                    "relative items-center m-0 max-h-2",
                    recording ? 'flex grow justify-end' : 'hidden'

                )}>
                {/* <canvas
                className='outline outline-red-500'
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        pointerEvents: 'none',
                        zIndex: 10, // Optional: control layering
                    }}
                    width={WIDTH} height={HEIGHT} ref={canvasRef as Ref<HTMLCanvasElement>} /> */}
                <IconButton
                    className={`select-none touch-manipulation  cursor-pointer rotate-180 bg-sky-400 hover:bg-sky-500 rounded-full stroke-white fill-white text-white p-2 z-20`}
                    onClick={async (e: any) => {
                        e.preventDefault();
                        stop(true);
                    }}

                />
            </div>
        </div>
    );
});

export default AudioVisualizer;